import type { ComponentType } from "react";
import { FolderOpen, ListTodo, MessageSquare, Settings, Sun, Wallet } from "lucide-react";

export type NavChild = {
  to: string;
  label: string;
  /** Only match pathname exactly (hub „Összefoglaló”). */
  exactPath?: boolean;
  /** Extra match beyond pathname (e.g. query). Implies exact path. */
  matchSearch?: (search: string) => boolean;
  badgeKey?: "pendingProposals";
};

export type NavGroup = {
  to: string;
  label: string;
  /** Mobile bottom bar only. Sidebar, menu, and aria-label keep `label`. */
  shortLabel?: string;
  Icon: ComponentType<{ className?: string | undefined }>;
  badgeKey?: "pendingProposals";
  /** Exact match for hub root (Ma). */
  end?: boolean;
  children?: NavChild[];
};

/** Visible bottom-bar caption. Full `label` stays on the sidebar and in aria-label. */
export function bottomBarLabel(group: NavGroup): string {
  return group.shortLabel ?? group.label;
}

function pathOf(to: string): string {
  return to.split("?")[0] ?? to;
}

/** Whether a child link is the current location. */
export function isNavChildActive(child: NavChild, pathname: string, search: string): boolean {
  const path = pathOf(child.to);
  const exact = pathname === path;
  const nested = pathname.startsWith(`${path}/`);
  if (child.matchSearch || child.exactPath) {
    if (!exact) return false;
    return child.matchSearch ? child.matchSearch(search) : true;
  }
  return exact || nested;
}

/** Whether any child (or the hub itself) is active — used to auto-expand. */
export function isNavGroupActive(group: NavGroup, pathname: string, search: string): boolean {
  if (pathname === group.to || pathname.startsWith(`${group.to}/`)) return true;
  for (const c of group.children ?? []) {
    if (isNavChildActive(c, pathname, search)) return true;
  }
  const related = RELATED_PREFIXES[group.to];
  if (related?.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return true;
  return false;
}

const RELATED_PREFIXES: Record<string, string[]> = {
  "/ma": ["/hataridok", "/atvetelek"],
  "/munka": ["/phases", "/contacts", "/ellenorzesek"],
  "/penz/kiadasok": ["/penz", "/budget", "/kiadasok", "/expenses", "/quotes", "/ajanlatok"],
  "/dokumentumok": ["/kolaudacio", "/documents", "/naplo", "/dozor", "/engedelyek"],
  "/asszisztens": ["/jovahagyas", "/bot", "/projekt-ai", "/_legacy/approvals", "/_legacy/ai"],
  "/beallitasok": ["/_legacy/phases", "/_legacy/dashboard"]
};

/** Single source for the mobile bottom bar and the desktop sidebar. Max 6. */
export const NAV_GROUPS: NavGroup[] = [
  {
    to: "/ma",
    label: "Ma",
    Icon: Sun,
    end: true,
    children: [
      { to: "/ma", label: "Összefoglaló", exactPath: true },
      { to: "/hataridok", label: "Határidők" },
      { to: "/atvetelek", label: "Átvételek" }
    ]
  },
  {
    to: "/munka",
    label: "Munka",
    Icon: ListTodo,
    children: [
      {
        to: "/munka",
        label: "Feladatok",
        matchSearch: (s) => !new URLSearchParams(s).get("nezet")
      },
      {
        to: "/munka?nezet=utem",
        label: "Ütem",
        matchSearch: (s) => new URLSearchParams(s).get("nezet") === "utem"
      },
      { to: "/contacts", label: "Kontaktok" },
      { to: "/ellenorzesek", label: "Ellenőrzések" }
    ]
  },
  {
    to: "/penz/kiadasok",
    label: "Pénz",
    Icon: Wallet,
    children: [
      { to: "/penz/kiadasok", label: "Kiadások" },
      { to: "/penz/rozpocet", label: "Rozpočet / Költségvetés", exactPath: true },
      { to: "/penz/rozpocet/parositas", label: "Párosítás" },
      { to: "/penz/ajanlatok", label: "Ajánlatok" },
      { to: "/penz/osszehasonlitas", label: "Összehasonlítás" },
      { to: "/penz/lekotesek", label: "Lekötések" },
      { to: "/penz/valtozasok", label: "Változások" },
      { to: "/penz/cashflow", label: "Cashflow" },
      { to: "/penz/elszamolas", label: "Elszámolandó" },
      { to: "/penz/szamlak", label: "Számlák" },
      { to: "/penz/letetek", label: "Letétek" },
      { to: "/penz/garancia", label: "Garancia" },
      { to: "/penz/egyenleg", label: "Egyenleg" },
      { to: "/penz/tervezes", label: "Tervezés" },
      { to: "/penz/rfq", label: "RFQ" },
      { to: "/penz/ertekeles", label: "Értékelés" },
      { to: "/penz/boq", label: "BOQ" },
      { to: "/lehivasok", label: "ŠFRB lehívás" },
      { to: "/budget", label: "Büdzsé" },
      { to: "/kiadasok/bizonylat-hiany", label: "Bizonylat-hiány" },
      { to: "/kiadasok/dph-ellenorzes", label: "DPH" }
    ]
  },
  {
    to: "/dokumentumok",
    label: "Dokumentumok",
    shortLabel: "Doksi",
    Icon: FolderOpen,
    children: [
      {
        to: "/dokumentumok",
        label: "Mind",
        matchSearch: (s) => {
          const t = new URLSearchParams(s).get("tipus");
          return !t;
        }
      },
      {
        to: "/dokumentumok?tipus=foto",
        label: "Fotó / fájl",
        matchSearch: (s) => new URLSearchParams(s).get("tipus") === "foto"
      },
      {
        to: "/dokumentumok?tipus=naplo",
        label: "Napló",
        matchSearch: (s) => new URLSearchParams(s).get("tipus") === "naplo"
      },
      { to: "/kolaudacio", label: "Kolaudáció" },
      { to: "/dozor", label: "Dozor" },
      { to: "/engedelyek", label: "Engedélyek" }
    ]
  },
  {
    to: "/asszisztens",
    label: "Asszisztens",
    shortLabel: "AI",
    Icon: MessageSquare,
    badgeKey: "pendingProposals",
    children: [
      {
        to: "/asszisztens",
        label: "Teendők",
        matchSearch: (s) => new URLSearchParams(s).get("mod") !== "kerdezz"
      },
      {
        to: "/asszisztens?mod=kerdezz",
        label: "Kérdezz",
        matchSearch: (s) => new URLSearchParams(s).get("mod") === "kerdezz"
      },
      {
        to: "/jovahagyas",
        label: "Jóváhagyásra vár",
        exactPath: true,
        badgeKey: "pendingProposals"
      },
      { to: "/bot", label: "Bot és Gmail" }
    ]
  },
  {
    to: "/beallitasok",
    label: "Beállítások",
    shortLabel: "Beáll.",
    Icon: Settings,
    children: [
      { to: "/beallitasok", label: "Áttekintés", exactPath: true },
      { to: "/beallitasok/ertesitesek", label: "Értesítések" },
      { to: "/beallitasok/bot-kulcsok", label: "Bot API kulcsok" },
      { to: "/beallitasok/ai", label: "AI plafon" },
      { to: "/beallitasok/megosztas", label: "Megosztás" },
      { to: "/beallitasok/anomaliak", label: "Anomáliák" },
      { to: "/beallitasok/naplo-audit", label: "Audit" },
      { to: "/beallitasok/adattisztitas", label: "Adattisztítás" }
    ]
  }
];
